/**
 * Application use cases: what screens call. Every function returns a typed Result
 * ({ ok: true, value } | { ok: false, reason, nextSlots? }) with machine-readable reasons that the
 * UX copy maps to messages. No React, no globals: build with createUseCases(deps).
 */
import { bookingUseCases } from './usecases/booking';
import { clientUseCases } from './usecases/clients';
import type { Deps } from './usecases/context';
import { maintenanceUseCases } from './usecases/maintenance';
import { paymentUseCases } from './usecases/payments';
import { onboardingUseCases } from './usecases/onboarding';
import { privacyUseCases } from './usecases/privacy';
import { viewUseCases } from './usecases/views';
import { waitlistUseCases } from './usecases/waitlist';

export type { Deps } from './usecases/context';
export type { BookAsClientInput, BookAsOwnerInput, BookingConfirmation, BookingPage, CancelOutcome, ManageView, MoveInput, OpenedSlot } from './usecases/booking';
export type { ClientExport, UpsertClientInput } from './usecases/clients';
export type { PaymentWarning } from './usecases/payments';
export type { DeletionOutcome, DeletionPreview, ExportFile, MyDataPreview, OwnerExport, OwnerExportJson } from './usecases/privacy';
export { DELETION_GRACE_DAYS, DELETION_POLICY } from './usecases/privacy';
export type { SchedulerReport } from './usecases/maintenance';
export type { CreateOwnerInput, OwnerAccount, PlanStatus } from './usecases/onboarding';
export type { AgendaItem, ManualQueueItem, TodayView } from './usecases/views';
export type { JoinWaitlistInput, OfferView, OpenedSlotCard } from './usecases/waitlist';
export { slugify } from './usecases/onboarding';

export function createUseCases(deps: Deps) {
  const onboarding = onboardingUseCases(deps);
  const booking = bookingUseCases(deps);
  const { findOrCreate: _f, syncConsents: _s, ...clients } = clientUseCases(deps);
  const paymentsUc = paymentUseCases(deps);
  const waitlist = waitlistUseCases(deps);
  const views = viewUseCases(deps, waitlist.openedSlotCards);
  const { expireWaitlist: _e, openedSlotCards: _o, ...waitlistPublic } = waitlist;
  const { purgeDueAccounts, ...privacy } = privacyUseCases(deps);
  const maintenance = maintenanceUseCases(deps, waitlist.expireWaitlist, purgeDueAccounts);
  return { ...onboarding, ...booking, ...paymentsUc, ...clients, ...privacy, ...waitlistPublic, ...views, ...maintenance };
}
export type UseCases = ReturnType<typeof createUseCases>;
